import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  SquareKanban,
  Shield,
  PanelLeft,
  ChevronsLeft,
  EyeOff,
} from 'lucide-react';
import { useUIStore } from '../../stores/uiStore.js';
import { useAuthStore } from '../../stores/authStore.js';
import { cn } from '../../lib/utils.js';
import { useModules, canAccessRole } from '../../hooks/useModules.js';
import { CORE_WINDOWABLE_PAGES } from '../../plugin/coreWindowablePages.js';
import { webExtensions } from '../../plugin/platformExtensions.js';
import { ModuleIcon } from '../icons/ModuleIcon.js';
import { useBuildVersion } from '../../hooks/useBuildVersion.js';

// Single source of truth for the console visibility gate: the SAME core-windowable entry the
// desktop launcher reads (coreWindowablePages). Deriving the required role here (instead of a
// second hardcoded 'admin') keeps the sidebar gate and the launcher gate from drifting (gh#1527).
const CONSOLE_WINDOWABLE = CORE_WINDOWABLE_PAGES.find((p) => p.pageRoute === '/console');

// Sidebar reads its module list from atlas via useModules() (#43).
// Dashboard is the only static entry — it's the meta-overview, not a
// platform module.

const toggleIcons = {
  expanded: ChevronsLeft,
  collapsed: EyeOff,
  hidden: PanelLeft,
};

const toggleTitles = {
  expanded: 'Daralt',
  collapsed: 'Gizle',
  hidden: 'Genişlet',
};

export function Sidebar() {
  const { sidebarState, toggleSidebar, setSidebarState } = useUIStore();
  const isExpanded = sidebarState === 'expanded';
  const isCollapsed = sidebarState === 'collapsed';
  const isHidden = sidebarState === 'hidden';

  // Atlas-bound modules; useModules already applies role filter and order
  const { modules } = useModules({ filterByRole: true, dashboardOnly: false });
  // User role for per-page child filtering (#335)
  const userRole = useAuthStore((s) => s.user?.role ?? 'guest');
  // Runtime build metadata — fetched once from /api/version (gh#877)
  const buildVersion = useBuildVersion();

  const ToggleIcon = toggleIcons[sidebarState];

  return (
    <>
      <aside
        data-testid="sidebar"
        className={cn(
          'fixed left-0 top-0 z-30 flex h-screen flex-col border-r border-gray-200 bg-white transition-all duration-300 ease-in-out dark:border-gray-700 dark:bg-gray-900',
          isExpanded && 'w-64 translate-x-0',
          isCollapsed && 'w-16 translate-x-0',
          isHidden && 'w-64 -translate-x-full',
        )}
      >
        {/* Logo */}
        <div className="flex h-16 items-center justify-between border-b border-gray-200 px-4 dark:border-gray-700">
          {isExpanded && (
            <span className="text-lg font-bold text-primary-700 dark:text-primary-400">Elmes</span>
          )}
          <button
            onClick={toggleSidebar}
            className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
            title={toggleTitles[sidebarState]}
          >
            <ToggleIcon className="h-5 w-5 transition-transform" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1 overflow-y-auto p-3">
          {/* Dashboard — static meta-overview, not a module */}
          <NavLink
            to="/dashboard"
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200',
                isCollapsed && 'justify-center px-2',
              )
            }
            title={isCollapsed ? 'Dashboard' : undefined}
          >
            <LayoutDashboard className="h-5 w-5 flex-shrink-0" />
            {isExpanded && <span>Dashboard</span>}
          </NavLink>

          {/* PM core module — static entry (route-based, not an atlas module yet).
              roadmap #452 / epic #447; windowable desktop-window is gh#1195. */}
          <NavLink
            to="/pm"
            data-testid="sidebar-pm"
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200',
                isCollapsed && 'justify-center px-2',
              )
            }
            title={isCollapsed ? 'Project Management' : undefined}
          >
            <SquareKanban className="h-5 w-5 flex-shrink-0" />
            {isExpanded && <span>Project Management</span>}
          </NavLink>

          {/* Entity OS Console — admin-gated static entry (gh#1527). The visibility gate derives
              from the SAME source the desktop launcher uses: the console CORE_WINDOWABLE_PAGES
              entry's requiredRole (single source of truth, no drift). A non-admin never sees the
              entry. The /console route itself is auth-gated only (ProtectedRoute); the real admin
              boundary is server-side (freshAdminGate) — this gate is the UI-visibility mirror. */}
          {CONSOLE_WINDOWABLE && canAccessRole(CONSOLE_WINDOWABLE.requiredRole, userRole) && (
            <NavLink
              to="/console"
              data-testid="sidebar-console"
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200',
                  isCollapsed && 'justify-center px-2',
                )
              }
              title={isCollapsed ? 'Console' : undefined}
            >
              <Shield className="h-5 w-5 flex-shrink-0" />
              {isExpanded && <span>Console</span>}
            </NavLink>
          )}

          {/* Build-time web slot: an extension's links to its own direct routes, behind the route's own role. */}
          {webExtensions.sidebar.filter((item) => canAccessRole(item.requiredRole, userRole)).map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              data-testid={`sidebar-extension-${item.path}`}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200',
                  isCollapsed && 'justify-center px-2',
                )
              }
              title={isCollapsed ? item.label : undefined}
            >
              <ModuleIcon name={item.icon?.name} className="h-5 w-5 flex-shrink-0" />
              {isExpanded && <span>{item.label}</span>}
            </NavLink>
          ))}

          {modules.map((mod) => {
            // Child pages visible in sidebar: showInSidebar not false, not duplicate
            // of module root path, and passes requiredRole check (#335)
            const sidebarPages = isCollapsed
              ? []
              : mod.pages.filter(
                  (p) =>
                    p.showInSidebar !== false &&
                    p.path !== mod.routePath &&
                    canAccessRole(p.requiredRole, userRole),
                );

            return (
              <div key={mod.slug} data-testid={`sidebar-module-${mod.slug}`}>
                {/* dm.issue 1014: unlike sidebarPages above, this module-level NavLink has
                    no pages/status filter — an HTTP-only manifest (pages:[]) still renders it */}
                <NavLink
                  to={mod.routePath}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200',
                      isCollapsed && 'justify-center px-2',
                    )
                  }
                  title={isCollapsed ? mod.title : undefined}
                >
                  <ModuleIcon name={mod.iconName} className="h-5 w-5 flex-shrink-0" />
                  {isExpanded && <span>{mod.title}</span>}
                </NavLink>

                {sidebarPages.map((page) => (
                  <NavLink
                    key={page.path}
                    to={page.path}
                    end
                    data-testid="sidebar-page"
                    data-page-path={page.path}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-2 rounded-lg pl-10 pr-3 py-1.5 text-sm transition-colors',
                        isActive
                          ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200',
                      )
                    }
                  >
                    <span>{page.label}</span>
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>

        {/* Version & build info */}
        <div className={cn('shrink-0 border-t border-gray-200 dark:border-gray-700 px-3 py-2', isCollapsed && 'px-1 text-center')}>
          {isExpanded ? (
            <>
              <p className="text-[10px] text-gray-400 dark:text-gray-500">
                {buildVersion.version ? `Elmes v${buildVersion.version}` : 'Elmes'}
                {buildVersion.build !== null && ` · Build #${buildVersion.build}`}
              </p>
              {buildVersion.timestamp && (
                <p className="text-[10px] text-gray-300 dark:text-gray-600">
                  {new Date(buildVersion.timestamp).toLocaleDateString('tr-TR')}{' '}
                  {new Date(buildVersion.timestamp).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
            </>
          ) : (
            buildVersion.build !== null && (
              <p className="text-[10px] text-gray-400 dark:text-gray-500">#{buildVersion.build}</p>
            )
          )}
        </div>
      </aside>

      {/* Floating reveal button — visible only when sidebar is hidden */}
      {isHidden && (
        <button
          onClick={() => setSidebarState('expanded')}
          className="fixed left-3 top-3 z-40 rounded-lg border border-gray-200 bg-white p-2 text-gray-500 shadow-md transition-all hover:bg-gray-100 hover:text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
          title="Sidebar'ı aç"
        >
          <PanelLeft className="h-5 w-5" />
        </button>
      )}
    </>
  );
}
